/**
 * W8 轻量错误上报（DIY，无第三方依赖）。
 *
 * 把前端的全局 JS 错误、未处理 Promise rejection,以及那些"best-effort"写入失败
 * （segment/start、round/end 等过去只 console.warn 吞掉的）打到 worker 的 /api/client-error,
 * 服务端结构化 console.error → `wrangler tail` 直接可见。
 *
 * 设计原则:绝不从上报里再抛错（包在 try/catch）；用 sendBeacon 保证页面卸载时也能送出。
 */
interface ErrorExtra {
  stack?: string;
  context?: string;
}

export function reportClientError(kind: string, message: string, extra?: ErrorExtra): void {
  try {
    const payload = JSON.stringify({
      kind,
      message: String(message ?? '').slice(0, 500),
      stack: extra?.stack?.slice(0, 1500),
      context: extra?.context?.slice(0, 500),
      url: typeof location !== 'undefined' ? location.href : '',
    });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon('/api/client-error', new Blob([payload], { type: 'application/json' }));
    } else {
      void fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'include',
      }).catch(() => {
        /* 上报失败也静默，绝不影响主流程 */
      });
    }
  } catch {
    /* never throw from telemetry */
  }
}

/** 在应用启动时挂全局兜底,捕获任何没被 try/catch 接住的错误。 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    reportClientError('window.error', e.message, {
      stack: (e.error as Error | undefined)?.stack,
      context: `${e.filename}:${e.lineno}:${e.colno}`,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined;
    reportClientError('unhandledrejection', reason?.message ?? String(e.reason), {
      stack: reason?.stack,
    });
  });
}
